window.ColorSiblingsShared = (function () {
  var FARVE_HEX = {
    'Sort': '#1a1a1a',
    'Hvid': '#ffffff',
    'Grå': '#9e9e9e',
    'Beige': '#e3d5b8',
    'Brun': '#6b4423',
    'Blå': '#2563eb',
    'Navy': '#1e2a4a',
    'Grøn': '#16a34a',
    'Gul': '#fbbf24',
    'Lilla': '#9333ea',
    'Lyserød': '#f9a8d4',
    'Orange': '#f97316',
    'Rød': '#dc2626'
  };

  // Splitting each farve_filter value into its own swatch dot (one product ->
  // several dots) was tried and reverted: once several sibling products each
  // had 2-3 values, the flattened list filled up with visually identical
  // dots (e.g. 4 different "Sort" dots) pointing at different products,
  // which read as broken/duplicated rather than informative. Back to one
  // swatch per sibling PRODUCT: a single color renders solid, several real
  // colors render as a proportional conic-gradient, and any unmapped
  // descriptor ("Mønstret", "Stribet", ...) collapses the whole swatch to a
  // plain 50/50 black/white placeholder regardless of what else is present.
  function buildSwatchStyle(farveValues) {
    var hexes = [];
    var hasUnmapped = false;
    for (var i = 0; i < farveValues.length; i++) {
      var hex = FARVE_HEX[farveValues[i]];
      if (hex) {
        hexes.push(hex);
      } else {
        hasUnmapped = true;
      }
    }
    if (hasUnmapped) return 'background: conic-gradient(#1a1a1a 0deg 180deg, #ffffff 180deg 360deg);';
    if (hexes.length === 0) return null;
    if (hexes.length === 1) return 'background-color: ' + hexes[0] + ';';
    var segment = 360 / hexes.length;
    var parts = hexes.map(function (hex, i) {
      return hex + ' ' + (segment * i) + 'deg ' + (segment * (i + 1)) + 'deg';
    });
    return 'background: conic-gradient(' + parts.join(',') + ');';
  }

  // Fetches every product in the catalog (paginated, not the fuzzy search index,
  // since some products aren't reliably indexed for text search). Cached per
  // shop+token in-memory so multiple swatch containers on one page share a
  // single fetch, AND in localStorage so the (currently ~18-request) full
  // catalog scan doesn't have to run again on every single page load — this
  // is a classic multi-page theme, not an SPA, so the in-memory cache alone
  // is wiped on every navigation. localStorage entries older than
  // STALE_AFTER_MS are still served immediately (so swatches never wait on
  // network), then silently refreshed in the background for the next load.
  var cache = {};
  var STALE_AFTER_MS = 10 * 60 * 1000;
  // Bump whenever the shape of a cached product node changes, so stale
  // localStorage entries from an older version get ignored instead of
  // silently missing fields the new matching logic relies on.
  var CATALOG_VERSION = 4;
  // Storefront API version — Shopify keeps a version live for roughly a
  // year after release, so this needs bumping periodically. Verify this is
  // still a supported version before relying on it.
  var API_VERSION = '2026-01';

  function readStoredCatalog(shopDomain) {
    try {
      var raw = localStorage.getItem('csCatalog:' + shopDomain);
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      return parsed.version === CATALOG_VERSION ? parsed : null;
    } catch (e) {
      return null;
    }
  }

  function writeStoredCatalog(shopDomain, products) {
    try {
      localStorage.setItem('csCatalog:' + shopDomain, JSON.stringify({ version: CATALOG_VERSION, fetchedAt: Date.now(), products: products }));
    } catch (e) {
      // localStorage full/unavailable — swatches still work, just without the cross-page cache.
    }
  }

  // Some products never got their custom.style_name metafield filled in during
  // import, even though they clearly have color data (e.g. a farve_filter set).
  // Liquid already falls back to the SKU prefix (the part before the first "-")
  // for the CURRENT product when the metafield is blank; apply the exact same
  // fallback here so those products can also be matched against — and, at
  // minimum, match themselves so their own color still renders instead of the
  // whole block silently disappearing.
  function resolveStyleNumber(node) {
    if (node.styleName && node.styleName.value) return node.styleName.value;
    if (node.sku) return node.sku.split('-')[0];
    return null;
  }

  // A single failed/throttled page used to silently truncate the whole catalog
  // scan (a transient blip on page 6 of 18 meant products on pages 7+ would
  // never match). Retry a few times with backoff before giving up on a page.
  async function fetchProductsPage(shopDomain, token, cursor) {
    var afterClause = cursor ? ', after: ' + JSON.stringify(cursor) : '';
    var query = 'query { products(first: 250' + afterClause + ') { pageInfo { hasNextPage } edges { cursor node { handle title availableForSale featuredImage { url(transform: { maxWidth: 200 }) } colorPattern: metafield(namespace: "shopify", key: "color-pattern") { references(first: 10) { nodes { ... on Metaobject { label: field(key: "label") { value } } } } } metafield(namespace: "custom", key: "colors_name") { value } farve: metafield(namespace: "custom", key: "farve_filter") { value } styleName: metafield(namespace: "custom", key: "style_name") { value } variants(first: 1) { edges { node { sku } } } } } } }';
    var maxAttempts = 3;

    for (var attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        var res = await fetch('https://' + shopDomain + '/api/' + API_VERSION + '/graphql.json', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token': token
          },
          body: JSON.stringify({ query: query })
        });
        var data = await res.json();
        var products = data && data.data && data.data.products;
        if (products) return products;
      } catch (e) {
        // fall through to retry
      }
      if (attempt < maxAttempts) {
        await new Promise(function (resolve) { setTimeout(resolve, attempt * 500); });
      }
    }

    return null;
  }

  function fetchAllProductsFromApi(shopDomain, token) {
    return (async function () {
      var all = [];
      var cursor = null;
      var hasNextPage = true;
      var pageCount = 0;
      var complete = false;

      // The catalog keeps growing (512 -> 3750+ products within days), so this
      // is a safety net against a runaway loop, not a real ceiling — it must
      // stay far above any realistic product count or newer products silently
      // stop getting matched to their color siblings.
      while (hasNextPage && pageCount < 200) {
        var products = await fetchProductsPage(shopDomain, token, cursor);
        if (!products) break; // exhausted retries — stop, but don't treat this as a complete scan

        var edges = products.edges;
        all = all.concat(edges.map(function (e) {
          var node = e.node;
          var variantEdges = node.variants && node.variants.edges;
          node.sku = variantEdges && variantEdges[0] ? variantEdges[0].node.sku : null;
          delete node.variants;
          return node;
        }));
        hasNextPage = products.pageInfo.hasNextPage;
        cursor = edges.length ? edges[edges.length - 1].cursor : null;
        pageCount++;

        if (!hasNextPage) complete = true;
      }

      // Only persist a scan that actually reached the end of the catalog —
      // caching a truncated list would make real siblings invisible for the
      // next 10 minutes on every page that reads the cache.
      if (complete) writeStoredCatalog(shopDomain, all);

      return all;
    })();
  }

  function getAllProducts(shopDomain, token) {
    var cacheKey = shopDomain + '|' + token;
    if (cache[cacheKey]) return cache[cacheKey];

    var stored = readStoredCatalog(shopDomain);

    if (stored && stored.products && stored.products.length) {
      cache[cacheKey] = Promise.resolve(stored.products);

      if (Date.now() - stored.fetchedAt > STALE_AFTER_MS) {
        fetchAllProductsFromApi(shopDomain, token); // refresh localStorage in the background for next load
      }

      return cache[cacheKey];
    }

    cache[cacheKey] = fetchAllProductsFromApi(shopDomain, token);
    return cache[cacheKey];
  }

  async function getSiblings(shopDomain, token, styleNumber) {
    var all = await getAllProducts(shopDomain, token);
    return all.filter(function (n) { return resolveStyleNumber(n) === styleNumber; });
  }

  // Each swatch gets its own persistent tooltip + arrow, positioned via
  // native CSS anchor positioning (anchor-name / position-anchor /
  // position-area) instead of one shared element repositioned via JS on
  // every hover. Elements are appended to <body> so a slider's
  // `overflow: hidden` never clips them; anchor positioning still finds the
  // anchor correctly even though the tooltip isn't a DOM descendant of it.
  var anchorSupported = typeof CSS !== 'undefined' && CSS.supports && CSS.supports('anchor-name: --cs-test');
  var anchorCounter = 0;

  function attachTooltip(target, text) {
    if (!anchorSupported) return;

    var anchorName = '--cs-anchor-' + (anchorCounter++);
    target.style.setProperty('anchor-name', anchorName);

    var tooltip = document.createElement('div');
    tooltip.className = 'cs-tooltip';
    tooltip.style.setProperty('position-anchor', anchorName);
    tooltip.textContent = text;

    var arrow = document.createElement('div');
    arrow.className = 'cs-tooltip-arrow';
    arrow.style.setProperty('position-anchor', anchorName);

    document.body.appendChild(tooltip);
    document.body.appendChild(arrow);

    target.addEventListener('mouseenter', function () {
      tooltip.classList.add('is-visible');
      arrow.classList.add('is-visible');
    });
    target.addEventListener('mouseleave', function () {
      tooltip.classList.remove('is-visible');
      arrow.classList.remove('is-visible');
    });
    target.addEventListener('focus', function () {
      tooltip.classList.add('is-visible');
      arrow.classList.add('is-visible');
    });
    target.addEventListener('blur', function () {
      tooltip.classList.remove('is-visible');
      arrow.classList.remove('is-visible');
    });
  }

  return {
    FARVE_HEX: FARVE_HEX,
    buildSwatchStyle: buildSwatchStyle,
    getAllProducts: getAllProducts,
    getSiblings: getSiblings,
    attachTooltip: attachTooltip
  };
})();
